<script setup lang="ts">
/**
 * UI-kit component
 * Category: Tutors
 * Name: TutorPhoto
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=3393-22634
 *
 * The box that holds a tutor photo: the crop and the placeholder; the image itself
 * comes through the default slot (preferred value: TutorAvatar). An empty slot shows
 * the surface/tertiary placeholder with the user icon — the intended empty state.
 * The box clips its slot content to the radius (deviation from Figma's non-clipping
 * root, accepted 2026-09-15): the TutorRating overhang still works because the host
 * (TutorProfile) places the badge OUTSIDE this box, in its own relative wrapper.
 * It carries no rating itself — placement and visibility belong to the host.
 * Sizes pair one-to-one with TutorAvatar: lg 88x88, md 72x72. 'auto' is a code-only
 * extension for the card usage: md up to 1023px, lg from media_desktop (the epic's
 * tablet-follows-mobile rule). No profile-page size exists — adding one means adding
 * it to both components of the pair at once (note 6).
 * Local implementation of the Figma DS component missing from @brighterly/lib-lumee-ui (dsGap).
 */
// types
import type { TutorPhotoProps } from '../types/interfaces'
// components
import { Icon } from '@brighterly/lib-lumee-ui'

defineOptions({
  name: 'TutorPhoto',
})

withDefaults(defineProps<TutorPhotoProps>(), {
  size: 'lg',
})
</script>

<template>
  <span
    class="tutor-photo"
    :class="`tutor-photo--${size}`"
  >
    <slot>
      <Icon
        name="user"
        size="lg"
        weight="solid"
        color="muted"
      />
    </slot>
  </span>
</template>

<style lang="scss" scoped src="../styles/_tutor-photo.scss" />
